import {
  Breakpoints,
  CTA,
  DesignTokenSizeEnum,
  Divider,
  Layout,
  Text,
  TextAlignment,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { Fragment, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { BookingCard } from './BookingCard';
import { BookingsPageViewProps } from './BookingsPageViewProps';

const StyledImageWrapper = styled.div`
  flex-grow: 1;
`;
const StyledImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const StyledCardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  .card-wrapper,
  .divider-wrapper {
    display: flex;
    flex: 0 0 auto;
    width: 100%;
  }

  .divider-wrapper {
    margin: ${(props) => `${props.theme.size.static400} 0`};

    &:last-child {
      display: none;
    }

    & hr {
      width: 100%;
    }
  }

  @media (min-width: ${Breakpoints.b960}px) {
    row-gap: ${(props) => `${props.theme.size.static510}`};

    .divider-wrapper {
      width: 1px;
      margin: ${(props) => `0 ${props.theme.size.static400}`};

      &:nth-child(4n) {
        display: none;
      }

      & hr {
        width: 1px;
        height: 100%;
      }
    }

    .card-wrapper {
      align-items: stretch;
      width: ${(props) => `calc(50% - (${props.theme.size.static400} + 1px))`};
    }
  };

  @media (min-width: ${Breakpoints.b1600}px) {
    .divider-wrapper {
      &:nth-child(4n) {
        display: initial;
      }

      &:nth-child(6n) {
        display: none;
      }
    }

    .card-wrapper {
      width: ${(props) => `calc(100% / 3 - (4 / 3) * (${props.theme.size.static400} + 1px))`};
    }
  };

  @media (min-width: ${Breakpoints.b1920}px) {
    .divider-wrapper {
      &:nth-child(4n) {
        display: initial;
      }

      &:nth-child(6n) {
        display: initial;
      }

      &:nth-child(8n) {
        display: none;
      }
    }

    .card-wrapper {
      width: ${(props) => `calc(100% / 4 - (6 / 4) * (${props.theme.size.static400} + 1px))`};
    }
  };
}
`;

const imageLayoutAppearance = {
  [Breakpoints.default]: [
    { name: '.', columns: 0 },
    { name: 'a', columns: 24 },
    { name: '.', columns: 0 },
  ],
  [Breakpoints.b560]: [
    { name: '.', columns: 4 },
    { name: 'a', columns: 16 },
    { name: '.', columns: 4 },
  ],
  [Breakpoints.b1600]: [
    { name: '.', columns: 8 },
    { name: 'a', columns: 8 },
    { name: '.', columns: 8 },
  ],
};

export function BookingsPageVwView({
  vehicleDetails,
  bookings,
  dealers,
  onAmendBooking,
  onCancelBooking,
  onBookNewService,
}: BookingsPageViewProps): ReactElement {
  const [vehicleImageDisplayed, setVehicleImageDisplayed] = useState(true);

  return (
    <Layout rowGap={DesignTokenSizeEnum.dynamic0250}>
      <Layout rowGap={DesignTokenSizeEnum.static150} appearance={imageLayoutAppearance}>
        <div>
          {vehicleImageDisplayed && (
            <StyledImageWrapper>
              <StyledImage
                src={`https://localhost:44322/api/v1/booking/vehicle/image?vin=${vehicleDetails.vin}&ratio=4/3`}
                onError={() => {
                  setVehicleImageDisplayed(false);
                }}
                alt="vehicle"
              />
            </StyledImageWrapper>
          )}
          <Text textAlign={TextAlignment.center} appearance={TokenTextAppearance.bigcopy100} bold>
            {vehicleDetails.modelDescription}
          </Text>
        </div>
      </Layout>
      <StyledCardsWrapper>
        {!!(bookings && dealers) &&
          bookings.map((booking, i) => {
            const dealer = dealers.find((x) => x.dealerCode === booking.dealerCode)!;

            return (
              <Fragment key={`card-${booking.bookingRef}`}>
                <div className="card-wrapper">
                  <BookingCard
                    booking={booking}
                    amendBooking={onAmendBooking}
                    cancelBooking={onCancelBooking}
                    dealer={dealer}
                    key={booking.bookingRef}
                  />
                </div>
                {i !== bookings.length - 1 && (
                  <div className="divider-wrapper">
                    <Divider />
                  </div>
                )}
              </Fragment>
            );
          })}
      </StyledCardsWrapper>
      <CTA
        tag="button"
        onClick={onBookNewService}
        stretchContent={{
          [Breakpoints.default]: true,
          [Breakpoints.b560]: false,
        }}>
        Book a new service
      </CTA>
    </Layout>
  );
}
